import type { RouteName } from './routes';

/**
 * Single source of truth for every dated roadmap promise on the site (#195).
 *
 * The owner-page sashes (`McpSection`, `DomainRoadmapSection`), the pricing
 * matrix tags, the About page's "shipping next" pills, the homepage FAQ
 * roadmap answer AND `/changelog` all read this module. Shipping an item is
 * a one-line edit — set `shipped` — which simultaneously:
 *   - removes it from every "upcoming"/ledger rendering (`upcoming()`),
 *   - retires the amber sash on its owner page,
 *   - powers the "promise kept" stamp on `/changelog` (entries reference
 *     items by id via their `promise` field).
 *
 * Dates follow docs-as-spec: `components/docs/data/nav.ts` etas are the
 * canonical promises for docs-owned items (guarded by lib/roadmap.test.ts),
 * and the docs changelog stub's shipped months anchor the `shipped` values.
 */
export type RoadmapId =
  | 'mcp'
  | 'email-forwarding'
  | 'nameservers'
  | 'csv-import'
  | 'takeout'
  | 'webhooks';

export interface RoadmapItem {
  id: RoadmapId;
  /** Ledger label — one line, no feature explanation (that's the owner page's job). */
  label: string;
  /** The public promise, worded exactly as the owner page's sash ('Sept 2026', 'Q4 2026'). */
  promised: string;
  /**
   * ISO ship date once live. ⚠ Day-level precision is a placeholder pending
   * owner confirmation (page brief §8.3) — the months are locked by the docs
   * changelog stub; the exact days are not yet confirmed.
   */
  shipped?: string;
  stage: 'in-progress' | 'planned';
  /** Owner page — where the sash lives and where the ledger row links. */
  route: RouteName;
}

export const ROADMAP: RoadmapItem[] = [
  {
    id: 'email-forwarding',
    label: 'Email forwarding on link domains',
    promised: 'Aug 2026',
    shipped: '2026-08-05',
    stage: 'in-progress',
    route: 'customDomains',
  },
  {
    id: 'mcp',
    label: 'MCP server — agent-driven link management',
    promised: 'Sept 2026',
    shipped: '2026-08-12',
    stage: 'in-progress',
    route: 'aiMcp',
  },
  {
    id: 'nameservers',
    label: 'Nameserver delegation',
    promised: 'Oct 2026',
    stage: 'in-progress',
    route: 'customDomains',
  },
  {
    id: 'csv-import',
    label: 'Bulk import (CSV)',
    promised: 'Q4 2026',
    stage: 'planned',
    route: 'docs',
  },
  {
    id: 'takeout',
    label: 'Workspace-wide data takeout',
    promised: 'Q4 2026',
    shipped: '2026-09-28',
    stage: 'planned',
    route: 'docs',
  },
  {
    id: 'webhooks',
    label: 'Outbound webhooks',
    promised: 'Q1 2027',
    stage: 'planned',
    route: 'developers',
  },
];

export function roadmapItem(id: RoadmapId): RoadmapItem {
  // Non-null: RoadmapId is a closed union and ROADMAP covers it (tested).
  return ROADMAP.find((r) => r.id === id)!;
}

const MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12,
};

/**
 * Last ISO day covered by a promise tag — 'Sept 2026' → '2026-09-30',
 * 'Q4 2026' → '2026-12-31'. This is what makes "0 SHIPPED LATE" a derived
 * fact instead of hand-typed prose: an item is late iff `shipped` is past
 * this deadline, and the stat chip flips the day that's ever true.
 */
export function promiseDeadline(promised: string): string {
  const q = promised.match(/^Q([1-4])\s+(\d{4})$/i);
  const parts = promised.trim().split(/\s+/);
  let year: number;
  let month: number;
  if (q) {
    year = Number(q[2]);
    month = Number(q[1]) * 3;
  } else {
    month = MONTHS[parts[0]?.toLowerCase()] ?? NaN;
    year = Number(parts[1]);
    if (!month || !Number.isFinite(year)) {
      throw new Error(`Unparseable promise tag: "${promised}"`);
    }
  }
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
}

/** First ISO day of a promise window — 'Sept 2026' → '2026-09-01'. */
export function promiseStart(promised: string): string {
  const deadline = promiseDeadline(promised);
  const q = promised.match(/^Q([1-4])\s+(\d{4})$/i);
  const startMonth = q ? (Number(q[1]) - 1) * 3 + 1 : Number(deadline.slice(5, 7));
  return `${deadline.slice(0, 4)}-${String(startMonth).padStart(2, '0')}-01`;
}

export const shippedLate = (item: RoadmapItem): boolean =>
  !!item.shipped && item.shipped > promiseDeadline(item.promised);

/** 'early' = shipped before the promised window opened; 'on-time' = within it. */
export function shipVerdict(item: RoadmapItem): 'early' | 'on-time' | 'late' | null {
  if (!item.shipped) return null;
  if (shippedLate(item)) return 'late';
  return item.shipped < promiseStart(item.promised) ? 'early' : 'on-time';
}

/** Ledger rows: everything not yet shipped, nearest deadline first. */
export const upcoming = (): RoadmapItem[] =>
  ROADMAP.filter((r) => !r.shipped).sort((a, b) =>
    promiseDeadline(a.promised).localeCompare(promiseDeadline(b.promised)),
  );
